import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useBrand } from '@/hooks/useBrand';
import { loadClientAiConversation } from '@/lib/coreClientChat';
import { useCart } from '@/hooks/useCart';
import { placeOrderFromCart, fetchClientOrders, type ClientOrderRow } from '@/lib/chatOrder';
import { getBalance } from '@/lib/wallet';
import { payOrdersWithWallet } from '@/lib/walletPay';
import QuickBookingModal from './QuickBookingModal';
import ServiceAvailabilityModal from './ServiceAvailabilityModal';
import { useFavorites } from '@/hooks/useFavorites';
import {
  isAppointmentsRequest,
  isBookingRequest,
  isCancelAppointmentRequest,
  isRescheduleAppointmentRequest,
  parseRequestedWhen,
  parseReschedule,
  matchAppointments,
  cancelClientAppointment,
  rescheduleClientAppointment,
  attemptBookingAcross,
  fetchClientAppointments,
  formatAppointmentLine,
  type BookableService,
  type ClientAppointment,
} from '@/lib/chatBooking';
import { trackAiChatEvents, trackAiChatEvent, notifyMerchantOnAiClick } from '@/lib/aiChatTracking';
import { extractChatMedia, firstChatImage } from '@/lib/aiChatMedia';
import { isOutOfStock, effectivePrice, type ChatSearchResult, type ChatBoutique } from '@/lib/chatResult';
import { fetchCommerceOpenState } from '@/lib/storeOpenStatus';
import { DEFAULT_STORE_LOGO, storeLogoOr } from '@/lib/defaultImages';
import ChatMediaGallery from './ChatMediaGallery';
import MerchantCardModal from './MerchantCardModal';
import TravelPackCard from './TravelPackCard';
import {
  buildTravelPack,
  isTravelPackRequest,
  reserveTravelPack,
  type TravelPack,
} from '@/lib/travelPack';
import VendorRatingBadge from '@/components/feature/marketplace/VendorRatingBadge';
import { fetchVendorRatings, type VendorRating } from '@/lib/vendorReviews';
import ChatLocationBadge from './ChatLocationBadge';
import VoiceInputButton from '@/components/feature/VoiceInputButton';
import SpeakButton from '@/components/feature/SpeakButton';
import ChatVoiceNote from './ChatVoiceNote';
import AiRichContent from '@/components/feature/AiRichContent';
import type { VoiceResult } from '@/hooks/useVoiceDictation';
import { uploadVoiceNote } from '@/lib/voiceStorage';
import {
  CURRENCIES,
  currencyInfo,
  normalizeCurrency,
  convertAmount,
  loadExchangeRates,
  getDisplayCurrency,
} from '@/lib/currency';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  results?: SearchResult[];
  /** Pack voyage composé par l'assistant (hébergement + guide + chauffeur…). */
  pack?: TravelPack;
  /** Pack déjà réservé (empêche un double envoi). */
  packReserved?: boolean;
  /** Note vocale : le message s'affiche comme un audio (réécoutable). */
  audio?: { url: string; duration: number };
}

type SearchResult = ChatSearchResult;

const SUGGESTIONS = [
  { icon: 'ri-flight-takeoff-line', text: 'Week-end à Fès, budget 2000 dirhams' },
  { icon: 'ri-scissors-line', text: 'Je veux me coiffer à Fès' },
  { icon: 'ri-hotel-line', text: 'Je veux aller à Marrakech visiter le désert' },
  { icon: 'ri-service-line', text: 'Je cherche un service de beauté' },
  { icon: 'ri-shopping-bag-3-line', text: 'Quels produits sont disponibles ?' },
  { icon: 'ri-calendar-check-line', text: "J'ai quel rendez-vous prévu ?" },
  { icon: 'ri-calendar-2-line', text: 'Déplace mon rendez-vous' },
  { icon: 'ri-truck-line', text: 'Où est ma commande ?' },
];

/** Action primary label per result type. */
function actionLabel(type: string): string {
  if (type === 'service_réservation') return 'Réserver';
  if (type === 'produit') return 'Commander';
  if (type === 'service') return 'Voir le service';
  return 'Ouvrir la boutique';
}

function resultIcon(type: string): string {
  if (type === 'produit') return 'ri-shopping-bag-3-line';
  if (type === 'service_réservation') return 'ri-calendar-check-line';
  if (type === 'boutique') return 'ri-store-2-line';
  return 'ri-service-line';
}

/** Type de favori du Core correspondant à un résultat du chat. */
function favoriteEntityType(type: string): string {
  if (type === 'produit') return 'product';
  if (type === 'boutique') return 'business';
  return 'service';
}

/** Date lisible en français (ex. « mardi 14 octobre »). */
function formatWhen(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch {
    return dateStr;
  }
}

/** Médias complets d'un résultat (images + vidéos). */
function resultMedia(r: SearchResult) {
  return extractChatMedia({ media: r.media, image: r.image, videos: r.videos });
}

/** Nombre de vidéos (indicateur de carte). */
function countVideos(r: SearchResult): number {
  return resultMedia(r).filter((m) => m.type === 'video').length;
}

/** Normalise (minuscules, sans accents) pour comparer des libellés. */
function foldText(value: string): string {
  return (value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Retrouve le résultat que le client vient de citer (« réserve-moi le service
 * Manucure Pédicure que je vois… ») en comparant son message aux résultats
 * affichés par le dernier message de l'assistant. Mémoire conversationnelle :
 * on résout le service visé au lieu de relancer une recherche qui partirait
 * dans une autre direction.
 */
function findReferencedResult(text: string, messages: ChatMessage[]): SearchResult | null {
  const t = foldText(text);
  if (!t) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg.role !== 'assistant' || !msg.results || msg.results.length === 0) continue;
    let best: { result: SearchResult; score: number } | null = null;
    for (const r of msg.results) {
      const words = foldText(r.name).split(/[^a-z0-9]+/).filter((w) => w.length >= 4);
      let score = 0;
      for (const w of words) if (t.includes(w)) score += 1;
      if (score > 0 && (!best || score > best.score)) best = { result: r, score };
    }
    return best ? best.result : null;
  }
  return null;
}

/** Détecte une demande explicite de passage de commande (et non une recherche). */
const ORDER_REQUEST_RE = /(passe|passes|passer|passez|pass\u00e9|pass\u00e9e|passent|passait|lance|lancent|lancer|lancez|valide|valides|valider|validez|finalise|finalises|finaliser|finalisez|confirme|confirmes|confirmer|confirmez|envoie|envoyes|envoyer|envoyez|effectue|effectues|effectuer|effectuez|place|places|placer|placez)(?:\s+(la|ma|mon|le|les|une|un|des|mes|nos))?\s*(commande|commandes|achats?|panier|cart)/i;
function isOrderRequest(text: string): boolean {
  if (ORDER_REQUEST_RE.test(text)) return true;
  // Variantes sans article : « lancer la commande », « passer commande », « finaliser ma commande »…
  return /(commander|valider|finaliser|passer|lancer|envoyer|effectuer)\s+(le|mon|ma|la)?\s*(panier|commande)/i.test(text);
}

/** Détecte une demande de SUIVI de commande (« où est ma commande ? »). */
const ORDER_TRACK_RE = /(o[\u00f9u]\s+(est|sont|en\s+est)|suivi|suivre|statut|\u00e9tat|etat|c'est\s+o[\u00f9u]\s+quand|retard).{0,40}(commande|colis|livraison|achat)|(commande|colis|livraison|achat).{0,40}(o[\u00f9u]\s+en\s+suis|en\s+est\s+o[\u00f9u]|suivi|statut|arrive\s+quand)|mes\s+commandes/i;
function isOrderTrackRequest(text: string): boolean {
  return ORDER_TRACK_RE.test(text);
}

/**
 * Détecte une demande d'AJOUT d'un produit au panier
 * (ex. « mets le produit X dans mon panier », « ajoute ça au panier »).
 * Dans ce cas, le site ajoute réellement le produit trouvé au panier — il ne
 * se contente plus d'afficher la fiche en laissant croire que c'est fait.
 */
const ADD_TO_CART_RE = /(ajoute|ajouter|rajoute|rajouter|mets|mettre|met|enregistre|garde)[\s\S]{0,60}?(dans|au|aux|à|a)?\s*(mon|le|la|ma|mes)?\s*(panier|cart)/i;
function isAddToCartRequest(text: string): boolean {
  return ADD_TO_CART_RE.test(text);
}

/**
 * Détecte une demande d'AFFICHAGE du panier
 * (ex. « affiche mon panier », « montre-moi mon panier », « voir mon panier »).
 * Distinct de l'ajout au panier : ici on ne cherche pas de produit, on montre
 * simplement le contenu du panier déjà constitué, sans quitter le chat.
 */
const SHOW_CART_RE = /(affiche|afficher|affiche-moi|montre|montrer|montre-moi|voir|vois|ouvre|ouvrir|consulte|consulter|regarde|regarder)[\s\S]{0,30}?(mon|le|la|ma|mes)?\s*(panier|cart)/i;
function isShowCartRequest(text: string): boolean {
  return SHOW_CART_RE.test(text);
}

/**
 * Détecte une COMMANDE DIRECTE désignant une offre précise
 * (« commande-moi le service Assurance maladie d'informaplus »,
 *  « achète ce produit », « je veux commander la carte entrepreneur »).
 * Contrairement à « passe ma commande » (qui valide le panier), ici le client
 * demande un contenu nommé : l'assistant doit l'ajouter au panier puis
 * demander confirmation avant d'envoyer la commande.
 */
function isDirectOrderRequest(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  if (isOrderRequest(t) || isOrderTrackRequest(t) || isShowCartRequest(t) || isAddToCartRequest(t)) return false;
  return /^(?:je\s+)?(?:(?:veux|voudrais|souhaite|peux[- ]tu|pouvez[- ]vous|merci\s+de)\s+)?(?:me\s+)?(?:commande[- ]moi|commandes[- ]moi|commander|commande|ach[eè]te[- ]moi|ach[eè]te|acheter|prends|prendre|r[eé]serve[- ]moi|r[eé]server)\b[\s\S]{3,}$/i.test(t)
    || /\b(?:commande[- ]moi|commandes[- ]moi|ach[eè]te[- ]moi|r[eé]serve[- ]moi)\b/i.test(t);
}

const ORDER_STATUS_LABELS: Record<string, string> = {
  'En attente': 'En attente',
  'en attente': 'En attente',
  'Confirmée': 'Confirmée',
  'Expédiée': 'Expédiée',
  'Livrée': 'Livrée',
  'Annulée': 'Annulée',
};

function orderStatusLabel(row: ClientOrderRow): string {
  const raw = row.statut_com_client || row.statut_com_vendeur || '';
  return ORDER_STATUS_LABELS[raw] || raw || 'En cours';
}

interface OrderLineItem {
  name: string;
  quantity: number;
  price?: number;
}

/**
 * Reconstitue les articles d'une commande : on privilégie `items_json`
 * (snapshot structuré) et on retombe sur `details` (« Produit ×2, ... »)
 * pour les commandes plus anciennes.
 */
function parseOrderItems(row: ClientOrderRow): OrderLineItem[] {
  const raw = row.items_json;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw
      .filter((it) => it && it.name)
      .map((it) => {
        const price = it.price != null && !Number.isNaN(Number(it.price)) ? Number(it.price) : undefined;
        return { name: String(it.name), quantity: Number(it.quantity) || 1, price };
      });
  }
  if (row.details) {
    return row.details
      .split(',')
      .map((chunk) => chunk.trim())
      .filter(Boolean)
      .map((chunk) => {
        const m = chunk.match(/^(.*?)\s*[\u00d7x]\s*(\d+)\s*$/i);
        if (m) return { name: m[1].trim(), quantity: parseInt(m[2], 10) };
        return { name: chunk, quantity: 1 };
      });
  }
  return [];
}

/** Bloc de suivi détaillé d'une commande : entête + un article par ligne. */
function formatOrderBlock(row: ClientOrderRow): string {
  const value = parseFloat(String(row.totalttc ?? row.sp ?? '0'));
  const amount = Number.isNaN(value) ? 0 : value;
  const cur = row.monaie || 'MAD';
  const money = `${amount.toLocaleString('fr-FR')} ${cur}`.trim();
  const date = row.date_time
    ? new Date(row.date_time).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  const label = row.titre || `Commande #${row.id}`;
  const head = `• #${row.id} — ${label}\n   ${orderStatusLabel(row)} · ${money}${date ? ` · ${date}` : ''}`;
  const items = parseOrderItems(row);
  if (items.length === 0) return head;
  const lines = items
    .map((it) => {
      const lineTotal = it.price != null ? ` — ${(it.price * it.quantity).toLocaleString('fr-FR')} ${cur}` : '';
      return `      – ${it.name} ×${it.quantity}${lineTotal}`;
    })
    .join('\n');
  return `${head}\n   Articles :\n${lines}`;
}

/** Formate un montant en gardant le code devise visible (ex. « 2 223 USD »). */
function moneyIn(amount: number, code: string): string {
  const c = normalizeCurrency(code);
  const decimals = ['JPY', 'XOF', 'XAF', 'GNF', 'KES', 'NGN', 'CDF'].includes(c) ? 0 : 2;
  const value = Number.isFinite(amount) ? amount : 0;
  return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: decimals })} ${c}`;
}

/**
 * Prix d'un résultat : par défaut on affiche la MONNAIE DU PRODUIT (référence).
 * Si le visiteur a choisi une devise d'affichage, on convertit (montant d'origine
 * en second). `main` est toujours renseigné.
 */
function convertParts(amount: number, native: string, display: string): { main: string; sub: string | null } {
  const from = normalizeCurrency(native || 'MAD');
  const to = display ? normalizeCurrency(display) : from;
  if (!display || to === from) return { main: moneyIn(amount, from), sub: null };
  const conv = convertAmount(amount, from, to);
  if (conv == null) return { main: moneyIn(amount, from), sub: null };
  return { main: moneyIn(conv, to), sub: moneyIn(amount, from) };
}

/** Extrait une adresse de livraison d'un message de commande. */
function extractDeliveryAddress(text: string): string {
  const m = text.match(/adresse\s*:?\s*(.+)$/i);
  if (m && m[1]) return m[1].trim().replace(/[.。]+$/, '');
  const m2 = text.match(/(?:livrer|livers|livraison|livrez)\s+(?:a|\u00e0|au|chez)?\s*:?\s*(.+)$/i);
  if (m2 && m2[1]) return m2[1].trim().replace(/[.。]+$/, '');
  return '';
}

interface ClientChatTabProps {
  /** Incrémenté par la barre de navigation pour ouvrir le panier du chat. */
  openCartSignal?: number;
}

/**
 * Action sur un rendez-vous en attente de confirmation de l'utilisateur.
 * On n'annule ni ne déplace jamais un RDV directement : on demande d'abord
 * « confirmez-vous ? » pour éviter un clic malheureux.
 */
type PendingApptAction =
  | { kind: 'cancel'; appt: ClientAppointment }
  | { kind: 'reschedule'; appt: ClientAppointment; date: string; time: string };

export default function ClientChatTab({ openCartSignal = 0 }: ClientChatTabProps) {
  const { user } = useAuth();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const { items, count, subtotal, currency, addToCart, removeFromCart, updateQuantity, clearCart } = useCart();
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: `Bonjour ! 👋 Je suis l'assistant IA ${brand.name}. Je connais toute la plateforme : services, réservations, boutiques, produits... Dites-moi ce que vous cherchez (par exemple « je veux me coiffer à Fès ») et je trouverai précisément ce qu'il vous faut, puis je peux vous aider à réserver.`,
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // Suivi de la fin de réponse : sert au badge « nouvelle réponse » de la bulle.
  const prevLoadingRef = useRef(false);
  const [selected, setSelected] = useState<SearchResult | null>(null);
  const [merchant, setMerchant] = useState<ChatBoutique | null>(null);
  const [booking, setBooking] = useState<{ id: number; name: string; date?: string; time?: string } | null>(null);
  // Panneau « Voir les disponibilités » d'un service réservable (calendrier ZCalendar).
  const [availability, setAvailability] = useState<{ id: number; name: string } | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutAddress, setCheckoutAddress] = useState('');
  const [placing, setPlacing] = useState(false);
  // Réservation d'un pack voyage en cours (index du message concerné).
  const [packBusy, setPackBusy] = useState<number | null>(null);
  // Paiement du panier avec le solde du portefeuille Zifek.
  const [useWalletPay, setUseWalletPay] = useState(false);
  const [wallet, setWallet] = useState<{ balance: number; currency: string } | null>(null);
  // Action sur un rendez-vous en attente de confirmation (« confirmez-vous ? »).
  const [pendingAction, setPendingAction] = useState<PendingApptAction | null>(null);
  // Devise d'affichage choisie dans le chat : on part de la devise d'affichage
  // globale du visiteur ('', = monnaie du produit/vendeur par défaut).
  const [chatCurrency, setChatCurrency] = useState<string>(() => getDisplayCurrency());
  const [currencyOpen, setCurrencyOpen] = useState(false);
  // Notes moyennes des boutiques affichées : rend le classement visible sur
  // les cartes du chat IA (note + volume d'avis).
  const [vendorRatings, setVendorRatings] = useState<Record<number, VendorRating>>({});
  const ratedIdsRef = useRef<Set<number>>(new Set());
  const chatEndRef = useRef<HTMLDivElement>(null);
  // Dernière requête du client (contexte pour les stats du marchand).
  const lastQueryRef = useRef('');
  const currencyRef = useRef<HTMLDivElement>(null);
  // Champ de saisie du chat : cible de l'insertion du texte dicté.
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // 🔔 Prévient la bulle flottante qu'une réponse vient d'arriver : elle affiche
  // alors le badge « Nouvelle réponse » si le panneau est fermé.
  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      window.dispatchEvent(new CustomEvent('zifek:assistant-replied'));
    }
    prevLoadingRef.current = loading;
  }, [loading]);
  useEffect(() => { setSelected(null); }, []);

  // Charge la note moyenne des boutiques présentes dans les résultats du chat
  // (une seule fois par boutique), pour l'afficher sur les cartes.
  useEffect(() => {
    const ids = new Set<number>();
    messages.forEach((m) => m.results?.forEach((r) => { if (r.boutique?.id) ids.add(r.boutique.id); }));
    const missing = [...ids].filter((id) => !ratedIdsRef.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => ratedIdsRef.current.add(id));
    let cancelled = false;
    fetchVendorRatings(missing).then((ratings) => {
      if (cancelled) return;
      setVendorRatings((prev) => ({ ...prev, ...ratings }));
    });
    return () => { cancelled = true; };
  }, [messages]);

  // Taux de change en direct (une seule fois) pour la conversion de devise du chat.
  useEffect(() => { loadExchangeRates(); }, []);

  // Ferme le sélecteur de devise au clic extérieur.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (currencyRef.current && !currencyRef.current.contains(e.target as Node)) setCurrencyOpen(false);
    };
    if (currencyOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [currencyOpen]);

  // Recharge le fil persistant depuis le Core (le chat survit au rafraîchissement).
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    loadClientAiConversation(user.id).then((conv) => {
      if (cancelled) return;
      if (conv.messages.length > 0) {
        setMessages(conv.messages as ChatMessage[]);
      }
    });
    return () => { cancelled = true; };
  }, [user?.id]);

  const pushAssistant = useCallback((content: string) => {
    setMessages((prev) => [...prev, { role: 'assistant', content }]);
  }, []);

  // Solde du portefeuille : sert à proposer (ou non) le paiement par solde.
  const refreshWallet = useCallback(async () => {
    if (!user?.id) { setWallet(null); return; }
    try {
      const balance = await getBalance(user.id);
      setWallet({ balance, currency: normalizeCurrency(user.monaie || 'MAD') });
    } catch {
      setWallet(null);
    }
  }, [user?.id, user?.monaie]);

  useEffect(() => { void refreshWallet(); }, [refreshWallet]);
  useEffect(() => {
    if (checkoutOpen) void refreshWallet();
  }, [checkoutOpen, refreshWallet]);

  // Ouverture du panier depuis la barre de navigation (sans quitter le compte).
  const lastCartSignalRef = useRef(0);
  useEffect(() => {
    if (!openCartSignal || openCartSignal === lastCartSignalRef.current) return;
    lastCartSignalRef.current = openCartSignal;
    setSelected(null);
    setMerchant(null);
    if (items.length > 0) {
      setCartOpen(true);
    } else {
      pushAssistant("🛒 Votre panier est vide pour l'instant. Dites-moi ce que vous cherchez (produit, service…) et je l'ajoute à votre panier.");
    }
  }, [openCartSignal, items.length, pushAssistant]);

  // Devise de RÉFÉRENCE d'un résultat : monnaie du produit, sinon celle du
  // commerce (vendeur), sinon celle du compte client. Jamais de devise inventée.
  const productCurrency = useCallback(
    (r: SearchResult) => normalizeCurrency(r.currency || r.boutique?.currency || user?.monaie || 'MAD'),
    [user],
  );

  // Ajoute un produit du chat au panier — SANS quitter le chat.
  // Vérifie d'abord que le commerce est OUVERT et que l'article est en stock.
  const addProductToCart = useCallback(async (r: SearchResult): Promise<boolean> => {
    // 🔒 Le commerce / vendeur doit être ouvert pour prendre une commande.
    if (r.boutique?.id) {
      const openState = await fetchCommerceOpenState(r.boutique.id);
      if (!openState.open) {
        pushAssistant(`🔒 ${r.boutique.name} est actuellement fermé${openState.todayLabel ? ` (${openState.todayLabel})` : ''}. Je ne peux pas ajouter cet article au panier pour l'instant — réessayez pendant ses heures d'ouverture.`);
        return false;
      }
    }
    // 📦 Rupture de stock (suivi activé) : on bloque aussi l'ajout.
    if (isOutOfStock(r)) {
      pushAssistant(`📦 « ${r.name} » est en rupture de stock pour le moment.`);
      return false;
    }
    const price = effectivePrice(r);
    // Stock réel (si suivi activé) pour brider la quantité dans le panier.
    const tracked = r.stock_tracking_enabled && r.product_type !== 'digital';
    addToCart({
      id: r.id,
      name: r.name,
      price,
      original_price: r.price ?? null,
      media: firstChatImage({ media: r.media, image: r.image, videos: r.videos }),
      stock: tracked ? (Number(r.stock ?? 0) || 0) : 0,
      product_type: r.product_type || 'physical',
      owner_id: r.boutique?.id,
      owner_name: r.boutique?.name,
      currency: r.currency || undefined,
    }, 1);
    // 📊 Statistiques marchand : ajout au panier depuis le chat IA.
    trackAiChatEvent({
      clientUserId: user?.id ?? null,
      clientName: user?.name ?? null,
      eventType: 'cart',
      productId: r.id,
      productName: r.name,
      merchantId: r.boutique?.id ?? null,
      query: lastQueryRef.current,
    });
    return true;
  }, [addToCart, user, pushAssistant]);

  // ── Favoris : enregistrer un résultat du chat d'un seul clic (cœur). ──
  const { isFav, toggle: toggleFav } = useFavorites();

  /**
   * Retrouve l'offre visée par une commande directe (« commande-moi le service
   * Assurance maladie d'informaplus ») : d'abord un contenu déjà affiché dans
   * la conversation, sinon le résultat dont le nom / la boutique correspond le
   * mieux au message.
   */
  const resolveOrderTarget = useCallback((text: string, results: SearchResult[]): SearchResult | null => {
    const referenced = findReferencedResult(text, messages);
    if (referenced) return referenced;

    const candidates = results.filter((r) => r.type === 'produit' || r.type === 'service' || r.type === 'service_réservation');
    if (candidates.length === 0) return null;

    const t = foldText(text);
    let best: SearchResult | null = null;
    let bestScore = 0;
    for (const r of candidates) {
      const words = foldText(r.name).split(/[^a-z0-9]+/).filter((w) => w.length >= 3);
      let score = 0;
      for (const w of words) if (t.includes(w)) score += 2;
      const shop = r.boutique?.name ? foldText(r.boutique.name) : '';
      if (shop && t.includes(shop)) score += 3;
      if (score > bestScore) { best = r; bestScore = score; }
    }
    // Aucune correspondance littérale : le backend a déjà classé par pertinence.
    return best || candidates[0];
  }, [messages]);

  /**
   * Ajoute l'offre visée au panier puis ouvre le récapitulatif de commande :
   * le client confirme explicitement avant que la commande soit envoyée.
   */
  const startOrderWithTarget = useCallback(async (
    target: SearchResult,
    results: SearchResult[],
  ): Promise<void> => {
    const shop = target.boutique?.name ? ` (${target.boutique.name})` : '';
    const already = items.some((i) => i.product.id === target.id);
    if (already) {
      setCartOpen(false);
      setCheckoutOpen(true);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `🧾 « ${target.name} »${shop} est déjà dans votre panier. Vérifiez le récapitulatif ci-dessous puis confirmez la commande.`,
        results,
      }]);
      return;
    }
    const added = await addProductToCart(target);
    if (!added) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `Je n'ai pas pu ajouter « ${target.name} » au panier (commerce fermé ou rupture de stock).`,
        results,
      }]);
      return;
    }
    setCartOpen(false);
    setCheckoutOpen(true);
    setMessages((prev) => [...prev, {
      role: 'assistant',
      content: `🧾 J'ai ajouté « ${target.name} »${shop} à votre panier. Vérifiez le récapitulatif ci-dessous, ajoutez votre adresse si besoin, puis confirmez la commande — rien n'est envoyé avant votre validation.`,
      results,
    }]);
  }, [items, addProductToCart]);

  const toggleFavoriteResult = useCallback(async (r: SearchResult) => {
    if (!user) return;
    try {
      await toggleFav({
        userId: user.id,
        entityType: favoriteEntityType(r.type),
        entityId: r.id,
        label: r.name,
        metadata: {
          image: firstChatImage({ media: r.media, image: r.image, videos: r.videos }) || null,
          ville: r.ville || r.boutique?.ville || null,
          price: effectivePrice(r) || null,
          currency: productCurrency(r),
          boutiqueName: r.boutique?.name || null,
          resultType: r.type,
        },
      });
    } catch { /* best-effort */ }
  }, [user, toggleFav, productCurrency]);

  /**
   * Compose et affiche un pack voyage complet (hébergement + guide + chauffeur
   * + table locale) dans le budget cité par le client.
   * Renvoie true si un pack a effectivement été proposé.
   */
  const handleTravelPack = useCallback(async (text: string): Promise<boolean> => {
    try {
      const built = await buildTravelPack(text);
      if (!built) return false;
      const { pack } = built;
      const parts = pack.items
        .map((i) => `• ${i.name}${i.merchantName ? ` (${i.merchantName})` : ''} — ${i.price.toLocaleString('fr-FR')} ${i.currency}`)
        .join('\n');
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `🗺️ J'ai composé un pack complet pour ${pack.destination} du ${formatWhen(pack.checkIn)} au ${formatWhen(pack.checkOut)} (${pack.nights} nuit${pack.nights > 1 ? 's' : ''}, ${pack.guests} personne${pack.guests > 1 ? 's' : ''}).\n\n${parts}\n\nTotal : ${pack.total.toLocaleString('fr-FR')} ${pack.currency}${pack.budget > 0 ? ` pour un budget de ${pack.budget.toLocaleString('fr-FR')} ${pack.currency}` : ''}.\n\nOuvrez le détail, puis cliquez sur « Tout réserver » : chaque commerce (logement, guide, chauffeur, restaurant) reçoit sa réservation.`,
          pack,
        },
      ]);
      return true;
    } catch {
      return false;
    }
  }, []);

  const handleSend = useCallback(async (raw?: string, audio?: { url: string; duration: number; blob?: Blob; mime?: string }) => {
    const text = (raw ?? input).trim();
    if (!text || loading || !user) return;

    // Un nouveau message annule toute confirmation de RDV en attente.
    setPendingAction(null);

    // Contexte conversationnel envoyé au backend (mémoire de la discussion).
    // Pour un vocal, `content` reste le TEXTE transcrit (mémoire IA), mais
    // l'affichage se fait sous forme de note audio réécoutable.
    const historyPayload = messages.map((m) => ({ role: m.role, content: m.content }));

    const userMsg: ChatMessage = audio
      ? { role: 'user', content: text, audio: { url: audio.url, duration: audio.duration } }
      : { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    lastQueryRef.current = text;
    setLoading(true);

    // 🎙️ Note vocale : on téléverse l'audio dans le stockage du compte (une seule
    // fois par envoi) pour qu'il reste réécoutable même après un rafraîchissement.
    // Le texte transcrit part à l'IA ; l'audio est joint pour la persistance.
    let voiceUrl: string | null = null;
    const ensureVoiceUrl = async (): Promise<string | null> => {
      if (!audio?.blob || !user?.id) return null;
      if (voiceUrl) return voiceUrl;
      try {
        voiceUrl = await uploadVoiceNote(audio.blob, user.id, audio.mime);
      } catch {
        voiceUrl = null;
      }
      return voiceUrl;
    };

    // ── Suivi de commande : l'assistant lit l'historique du client. ──
    // Tout se passe dans le fil, réponse immédiate (pas d'appel IA nécessaire).
    if (isOrderTrackRequest(text)) {
      try {
        const orders = await fetchClientOrders({
          id: user.id,
          name: user.name,
          email: user.email,
          telephone: user.telephone,
        });
        if (orders.length === 0) {
          pushAssistant("📦 Je ne trouve encore aucune commande sur votre compte. Dès que vous passez une commande (ici même ou sur une boutique Zifek), son suivi apparaîtra dans cette conversation.");
        } else {
          const lines = orders.slice(0, 6).map(formatOrderBlock).join('\n\n');
          const first = orders[0];
          const head = `📦 Voici vos dernières commandes :\n\n${lines}`;
          const foot = `\n\nDernière commande : ${orderStatusLabel(first)}. Ajoutez des articles au panier puis dites-moi « passe ma commande » pour une nouvelle commande.`;
          pushAssistant(head + foot);
        }
      } catch {
        pushAssistant("❌ Je n'ai pas pu récupérer vos commandes pour le moment. Réessayez dans un instant.");
      }
      setLoading(false);
      return;
    }

    // ── Annulation d'un rendez-vous (« annule mon RDV de mardi »). ──
    if (isCancelAppointmentRequest(text)) {
      try {
        const appts = await fetchClientAppointments(user.email, user.telephone);
        if (appts.length === 0) {
          pushAssistant("📅 Je ne vois aucun rendez-vous à venir sur votre compte, il n'y a donc rien à annuler.");
        } else {
          const when = parseRequestedWhen(text);
          let matched = matchAppointments(appts, when);
          if (when.time) {
            const byTime = matched.filter((a) => a.time.slice(0, 5) === when.time);
            if (byTime.length > 0) matched = byTime;
          }
          if (matched.length === 0) {
            const lines = appts.slice(0, 8).map(formatAppointmentLine).join('\n');
            pushAssistant(`🔎 Je ne trouve pas de rendez-vous correspondant à ce jour. Voici vos rendez-vous à venir :\n\n${lines}\n\nPrécisez le jour exact à annuler.`);
          } else if (matched.length > 1) {
            const lines = matched.map(formatAppointmentLine).join('\n');
            pushAssistant(`📅 Vous avez plusieurs rendez-vous ce jour-là :\n\n${lines}\n\nPrécisez l'heure (ex. « annule mon RDV de mardi 17h30 ») pour que j'annule le bon.`);
          } else {
            const target = matched[0];
            const place = [target.merchantName, target.ville].filter(Boolean).join(', ');
            setPendingAction({ kind: 'cancel', appt: target });
            pushAssistant(`🗓️ Vous êtes sur le point d'ANNULER ce rendez-vous :\n\n• ${formatWhen(target.date)} à ${target.time} — ${target.serviceName}${place ? ` (${place})` : ''}\n\nConfirmez-vous l'annulation ?`);
          }
        }
      } catch {
        pushAssistant("❌ Je n'ai pas pu annuler le rendez-vous pour le moment. Réessayez dans un instant.");
      }
      setLoading(false);
      return;
    }

    // ── Déplacement d'un rendez-vous (« déplace mon RDV de mardi à jeudi 15h »). ──
    if (isRescheduleAppointmentRequest(text)) {
      try {
        const appts = await fetchClientAppointments(user.email, user.telephone);
        if (appts.length === 0) {
          pushAssistant("📅 Je ne vois aucun rendez-vous à venir sur votre compte. Demandez-moi par exemple « réserve-moi un dentiste à Fès mardi à 17h30 ».");
        } else {
          const parsed = parseReschedule(text);
          const hasSource = Boolean(parsed.sourceDate || parsed.sourceDayLabel);
          const source = hasSource
            ? matchAppointments(appts, { date: parsed.sourceDate, dayLabel: parsed.sourceDayLabel })
            : appts;
          if (source.length === 0) {
            const lines = appts.slice(0, 8).map(formatAppointmentLine).join('\n');
            pushAssistant(`🔎 Je ne trouve pas le rendez-vous à déplacer. Voici vos rendez-vous à venir :\n\n${lines}\n\nPrécisez lequel (ex. « déplace mon RDV de mardi à jeudi 15h »).`);
          } else if (source.length > 1) {
            const lines = source.map(formatAppointmentLine).join('\n');
            pushAssistant(`📅 Vous avez plusieurs rendez-vous :\n\n${lines}\n\nPrécisez lequel déplacer (ex. « déplace mon RDV de mardi à jeudi 15h »).`);
          } else {
            const target = source[0];
            const targetDate = parsed.targetDate || target.date;
            const targetTime = parsed.targetTime;
            if (!targetTime) {
              pushAssistant(`📅 Vers quelle heure souhaitez-vous déplacer « ${target.serviceName} » (le ${formatWhen(targetDate)}) ? Ex. « à 15h ».`);
            } else {
              const place = target.merchantName ? ` (${target.merchantName})` : '';
              setPendingAction({ kind: 'reschedule', appt: target, date: targetDate, time: targetTime });
              pushAssistant(`🗓️ Vous êtes sur le point de DÉPLACER ce rendez-vous :\n\n• ${target.serviceName}${place}\n   ${formatWhen(target.date)} à ${target.time} → ${formatWhen(targetDate)} à ${targetTime}\n\nConfirmez-vous le déplacement ?`);
            }
          }
        }
      } catch {
        pushAssistant("❌ Je n'ai pas pu déplacer le rendez-vous pour le moment. Réessayez dans un instant.");
      }
      setLoading(false);
      return;
    }

    // ── Rendez-vous du client : « j'ai quel rendez-vous prévu ? ». ──
    if (isAppointmentsRequest(text)) {
      try {
        const appts = await fetchClientAppointments(user.email, user.telephone);
        if (appts.length === 0) {
          pushAssistant("📅 Je ne vois aucun rendez-vous à venir sur votre compte. Demandez-moi par exemple « réserve-moi un dentiste à Fès mardi à 17h30 » et je m'en occupe.");
        } else {
          const lines = appts.slice(0, 8).map(formatAppointmentLine).join('\n');
          pushAssistant(`📅 Voici vos prochains rendez-vous :\n\n${lines}\n\nSouhaitez-vous en ajouter un autre ?`);
        }
      } catch {
        pushAssistant("❌ Je n'ai pas pu récupérer vos rendez-vous pour le moment.");
      }
      setLoading(false);
      return;
    }

    // ── Voyage / week-end : l'assistant compose un PACK COMPLET. ──
    // Hébergement + guide + chauffeur + table locale, dans le budget cité,
    // puis réservation de toutes les prestations en un clic.
    if (isTravelPackRequest(text)) {
      const handled = await handleTravelPack(text);
      if (handled) {
        setLoading(false);
        return;
      }
    }

    // ── Prise de rendez-vous par l'IA (« réserve-moi un dentiste mardi 17h30 »). ──
    // L'IA trouve le meilleur prestataire, vérifie son calendrier ZCalendar et
    // réserve le créneau demandé — ou propose le plus proche.
    if (isBookingRequest(text)) {
      try {
        const { date, time } = parseRequestedWhen(text);

        // 🧠 Contexte conversationnel : si le client désigne un service qu'il
        // vient de VOIR (« réserve-moi le service X… »), on résout CE service
        // au lieu de relancer une recherche qui pourrait partir ailleurs.
        const referenced = findReferencedResult(text, messages);
        if (referenced) {
          const placeRef = referenced.boutique?.name ? ` (${referenced.boutique.name})` : '';
          if (referenced.type === 'service_réservation') {
            if (!date || !time) {
              setMessages((prev) => [...prev, {
                role: 'assistant',
                content: `Très bien, je note « ${referenced.name} »${placeRef}. Dites-moi un jour et une heure (ex. « mardi à 17h30 ») et je réserve pour vous.`,
                results: [referenced],
              }]);
              setLoading(false);
              return;
            }
            const bookable: BookableService = {
              id: referenced.id,
              name: referenced.name,
              duration: referenced.duration || 30,
              owner: referenced.boutique?.id ?? null,
              merchantName: referenced.boutique?.name ?? null,
            };
            const outcome = await attemptBookingAcross([bookable], date, time, {
              id: user.id, name: user.name, email: user.email, telephone: user.telephone,
            });
            if (outcome.ok) {
              setMessages((prev) => [...prev, {
                role: 'assistant',
                content: `✅ C'est réservé ! ${outcome.time} le ${formatWhen(outcome.date)} — ${outcome.serviceName}${outcome.merchantName ? ` (${outcome.merchantName})` : ''}. Vous recevrez une confirmation.`,
                results: [referenced],
              }]);
            } else if (outcome.reason === 'no_slot' && outcome.alternatives && outcome.alternatives.length > 0) {
              setMessages((prev) => [...prev, {
                role: 'assistant',
                content: `Le créneau ${time} du ${formatWhen(date)} n'est pas libre chez ${outcome.serviceName}. Créneaux les plus proches : ${outcome.alternatives.join(', ')}. Dites-moi lequel vous convient.`,
                results: [referenced],
              }]);
            } else if (outcome.reason === 'no_alternative' && outcome.nextDates && outcome.nextDates.length > 0) {
              const lines = outcome.nextDates.map((n) => `${formatWhen(n.date)} : ${n.slots.join(', ')}`).join(' — ');
              setMessages((prev) => [...prev, {
                role: 'assistant',
                content: `Aucun créneau le ${formatWhen(date)} chez ${outcome.serviceName}. Prochaines disponibilités : ${lines}. Indiquez-moi votre choix.`,
                results: [referenced],
              }]);
            } else {
              setMessages((prev) => [...prev, {
                role: 'assistant',
                content: `Je n'ai pas pu finaliser la réservation de « ${referenced.name} » à ce créneau. Proposez-moi un autre jour ou une autre heure.`,
                results: [referenced],
              }]);
            }
            setLoading(false);
            return;
          }
          // Service / produit non réservable en ligne (pas de calendrier ZCalendar).
          const contact = referenced.boutique?.telephone ? ` (${referenced.boutique.telephone})` : '';
          setMessages((prev) => [...prev, {
            role: 'assistant',
            content: `« ${referenced.name} » n'est pas réservable en ligne directement.${referenced.boutique?.name ? ` Contactez ${referenced.boutique.name}${contact}` : ' Contactez le commerçant'} pour fixer un rendez-vous.`,
            results: [referenced],
          }]);
          setLoading(false);
          return;
        }

        const voiceAudioUrl = await ensureVoiceUrl();
        const { data } = await supabase.functions.invoke('zifek-client-chat', {
          body: {
            message: text,
            userId: user.id,
            history: historyPayload,
            userCity: user["Ville"] || '',
            userCountry: user["Pays"] || '',
            audio: voiceAudioUrl ? { url: voiceAudioUrl, duration: audio?.duration ?? 0, mime: audio?.mime ?? null } : undefined,
          },
        });
        const reply = (data?.reply as string) || '';
        const results = ((data?.results || []) as SearchResult[]);
        const bookingResults = results.filter((r) => r.type === 'service_réservation');

        if (bookingResults.length === 0) {
          setMessages((prev) => [...prev, {
            role: 'assistant',
            content: reply || "Je n'ai pas trouvé de prestataire réservable pour cette demande. Précisez la ville ou le type de professionnel ?",
            results,
          }]);
          setLoading(false);
          return;
        }

        const bookables: BookableService[] = bookingResults.map((r) => ({
          id: r.id,
          name: r.name,
          duration: r.duration || 30,
          owner: r.boutique?.id ?? null,
          merchantName: r.boutique?.name ?? null,
        }));

        if (!date || !time) {
          setMessages((prev) => [...prev, {
            role: 'assistant',
            content: `J'ai trouvé ${bookingResults.length} prestataire${bookingResults.length > 1 ? 's' : ''} réservable${bookingResults.length > 1 ? 's' : ''}. Dites-moi un jour et une heure (ex. « mardi à 17h30 ») et je réserve pour vous.`,
            results,
          }]);
          setLoading(false);
          return;
        }

        const outcome = await attemptBookingAcross(bookables, date, time, {
          id: user.id,
          name: user.name,
          email: user.email,
          telephone: user.telephone,
        });

        if (outcome.ok) {
          const place = outcome.merchantName ? ` (${outcome.merchantName})` : '';
          setMessages((prev) => [...prev, {
            role: 'assistant',
            content: `✅ C'est réservé ! ${outcome.time} le ${formatWhen(outcome.date)} — ${outcome.serviceName}${place}. Vous recevrez une confirmation. Dites-moi « mes rendez-vous » pour revoir votre agenda.`,
            results,
          }]);
        } else if (outcome.reason === 'no_slot' && outcome.alternatives && outcome.alternatives.length > 0) {
          setMessages((prev) => [...prev, {
            role: 'assistant',
            content: `Le créneau ${time} du ${formatWhen(date)} n'est pas libre chez ${outcome.serviceName}. Voici les créneaux les plus proches ce jour-là : ${outcome.alternatives.join(', ')}. Dites-moi lequel vous convient et je réserve.`,
            results,
          }]);
        } else if (outcome.reason === 'no_alternative' && outcome.nextDates && outcome.nextDates.length > 0) {
          const lines = outcome.nextDates.map((n) => `${formatWhen(n.date)} : ${n.slots.join(', ')}`).join(' — ');
          setMessages((prev) => [...prev, {
            role: 'assistant',
            content: `Aucun créneau le ${formatWhen(date)} chez ${outcome.serviceName}. Voici les prochaines disponibilités : ${lines}. Indiquez-moi votre choix et je réserve.`,
            results,
          }]);
        } else if (outcome.reason === 'no_calendar') {
          setMessages((prev) => [...prev, {
            role: 'assistant',
            content: "Ce prestataire n'a pas encore activé son calendrier de réservation en ligne. Je vous affiche les offres trouvées — vous pouvez le contacter directement.",
            results,
          }]);
        } else {
          setMessages((prev) => [...prev, {
            role: 'assistant',
            content: "Je n'ai pas pu finaliser la réservation. Réessayez avec un autre créneau ou un autre prestataire.",
            results,
          }]);
        }
      } catch {
        pushAssistant("❌ Je n'ai pas réussi à réserver pour le moment. Réessayez dans un instant.");
      }
      setLoading(false);
      return;
    }

    // ── Affichage du panier à la demande (« affiche mon panier »). ──
    // On montre le contenu du panier déjà constitué, sans quitter le chat.
    if (!isAddToCartRequest(text) && isShowCartRequest(text)) {
      setSelected(null);
      setMerchant(null);
      if (items.length > 0) {
        setCartOpen(true);
        const lines = items
          .map((it) => `• ${it.quantity} × ${it.product.name} — ${convertParts(it.product.price * it.quantity, it.product.currency || currency, chatCurrency).main}`)
          .join('\n');
        pushAssistant(`🛒 Voici votre panier :\n\n${lines}\n\nTotal : ${convertParts(subtotal, currency, chatCurrency).main}\n\nDites-moi « passe ma commande » pour finaliser.`);
      } else {
        pushAssistant("🛒 Votre panier est vide pour l'instant. Dites-moi ce que vous cherchez (produit, service…) et je l'ajoute à votre panier.");
      }
      setLoading(false);
      return;
    }

    // ── Demande de passage de commande : tout se passe DANS le chat. ──
    // On prend les articles du panier, on crée la commande (Circuit du Core)
    // et on confirme dans le fil. Aucune redirection hors du chat.
    if (isOrderRequest(text)) {
      if (items.length === 0) {
        // Panier vide : le message désigne peut-être une offre précise
        // (« passe la commande du service Assurance maladie d'informaplus »).
        // On la retrouve puis on la met au panier au lieu de répondre « vide ».
        const referenced = findReferencedResult(text, messages);
        if (referenced && referenced.type !== 'boutique') {
          await startOrderWithTarget(referenced, [referenced]);
          setLoading(false);
          return;
        }
        try {
          const { data } = await supabase.functions.invoke('zifek-client-chat', {
            body: {
              message: text,
              userId: user.id,
              history: historyPayload,
              userCity: user["Ville"] || '',
              userCountry: user["Pays"] || '',
            },
          });
          const results = (data?.results || []) as SearchResult[];
          const target = results.length > 0 ? resolveOrderTarget(text, results) : null;
          if (target) {
            await startOrderWithTarget(target, results);
          } else {
            pushAssistant("🛒 Votre panier est vide et je n'ai pas identifié l'offre à commander. Nommez le produit ou le service (ex. « commande-moi le service Assurance maladie d'informaplus »).");
          }
        } catch {
          pushAssistant("🛒 Votre panier est vide pour l'instant. Nommez le produit ou le service à commander et je l'ajoute.");
        }
        setLoading(false);
        return;
      }
      // On n'envoie PLUS la commande immédiatement : on affiche d'abord un
      // récapitulatif du panier avec un bouton « Confirmer la commande ».
      const deliveryAddress = extractDeliveryAddress(text);
      if (deliveryAddress) setCheckoutAddress(deliveryAddress);
      setCartOpen(false);
      setCheckoutOpen(true);
      pushAssistant("🧾 Voici le récapitulatif de votre panier. Vérifiez les articles, ajoutez votre adresse de livraison si besoin, puis cliquez sur « Confirmer la commande » ci-dessous.");
      setLoading(false);
      return;
    }

    try {
      const voiceAudioUrl = await ensureVoiceUrl();
      const { data, error } = await supabase.functions.invoke('zifek-client-chat', {
        body: {
          message: text,
          userId: user.id,
          history: historyPayload,
          userCity: user["Ville"] || '',
          userCountry: user["Pays"] || '',
          audio: voiceAudioUrl ? { url: voiceAudioUrl, duration: audio?.duration ?? 0, mime: audio?.mime ?? null } : undefined,
        },
      });

      if (error) throw error;

      const reply = data?.reply || "Désolé, je n'ai pas pu analyser votre demande.";
      const results = (data?.results || []) as SearchResult[];

      // 📊 Statistiques marchand : on journalise l'affichage des produits
      // du chat IA (avec l'id du client qui a fait la recherche).
      const productResults = results.filter((r) => r.type === 'produit');
      if (productResults.length > 0) {
        void trackAiChatEvents(productResults.map((r) => ({
          clientUserId: user.id,
          clientName: user.name,
          eventType: 'display' as const,
          productId: r.id,
          productName: r.name,
          merchantId: r.boutique?.id ?? null,
          query: text,
        })));
      }

      // 🛒 Commande directe désignant une offre précise
      // (« commande-moi le service Assurance maladie d'informaplus ») :
      // on l'ajoute réellement au panier puis on demande confirmation.
      if (isDirectOrderRequest(text)) {
        const target = resolveOrderTarget(text, results);
        if (target) {
          await startOrderWithTarget(target, results);
        } else {
          setMessages((prev) => [...prev, {
            role: 'assistant',
            content: reply || "Je n'ai pas identifié précisément l'offre à commander. Précisez le nom du produit ou du service.",
            results,
          }]);
        }
        setLoading(false);
        return;
      }

      // 🛒 Si le client demande explicitement de mettre un produit au panier,
      // on l'ajoute RÉELLEMENT (au lieu de laisser l'IA prétendre l'avoir fait).
      const inStockProducts = productResults.filter((r) => !isOutOfStock(r));
      if (isAddToCartRequest(text) && inStockProducts.length > 0) {
        const target = inStockProducts[0];
        const added = await addProductToCart(target);
        if (added) {
          const shop = target.boutique?.name ? ` de ${target.boutique.name}` : '';
          const extra = inStockProducts.length > 1
            ? ` (${inStockProducts.length} produits correspondaient, j'ai pris le plus pertinent)`
            : '';
          setMessages((prev) => [...prev, {
            role: 'assistant',
            content: `🛒 « ${target.name} »${shop} a bien été ajouté à votre panier${extra}. Vérifiez le panier ci-dessus, puis dites-moi « passe ma commande » quand vous êtes prêt.`,
            results,
          }]);
        } else {
          setMessages((prev) => [...prev, { role: 'assistant', content: reply, results }]);
        }
      } else {
        setMessages((prev) => [...prev, { role: 'assistant', content: reply, results }]);
      }
    } catch {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: '❌ Désolé, une erreur est survenue. Veuillez réessayer.',
      }]);
    }
    setLoading(false);
  }, [input, loading, user, messages, items, subtotal, currency, chatCurrency, clearCart, pushAssistant, addProductToCart, resolveOrderTarget, startOrderWithTarget, handleTravelPack]);

  // Confirmation finale : c'est ici (et seulement ici) que la commande du chat
  // IA est réellement créée, après validation du récapitulatif par le client.
  const confirmCheckout = useCallback(async () => {
    if (!user || items.length === 0 || placing) return;
    setPlacing(true);
    const total = items.reduce((s, i) => s + i.product.price * i.quantity, 0);
    const itemCount = items.reduce((s, i) => s + i.quantity, 0);
    const deliveryAddress = checkoutAddress.trim();
    const result = await placeOrderFromCart({
      items,
      currency,
      buyer: {
        id: user.id,
        name: user.name,
        email: user.email,
        telephone: user.telephone,
        address: deliveryAddress,
      },
    });
    if (result.ok) {
      const ids = result.orderIds.map((id) => `#${id}`).join(', ');
      const addrLine = deliveryAddress
        ? `\n📍 Adresse de livraison transmise : ${deliveryAddress}.`
        : "\n💬 Indiquez-moi votre adresse de livraison (ex. « adresse : 12 rue… ») et je la transmets aux marchands.";

      // 💳 Paiement avec le solde du portefeuille : le débit est fait côté
      // serveur, après vérification du montant réel des commandes.
      let walletLine = '';
      if (useWalletPay && result.orderIds.length > 0) {
        const payment = await payOrdersWithWallet({ userId: user.id, orderIds: result.orderIds });
        if (payment.ok) {
          walletLine = payment.total
            ? `\n💳 Payée avec votre solde Zifek — ${payment.total.toLocaleString('fr-FR')} ${payment.currency || ''}.`.replace(/\s+\./, '.')
            : '\n💳 Commande déjà réglée avec votre solde Zifek.';
        } else {
          walletLine = `\n⚠️ Commande enregistrée, mais le paiement par solde a échoué : ${payment.error || 'réessayez depuis votre portefeuille.'}`;
        }
        await refreshWallet();
      }

      pushAssistant(`✅ Commande confirmée${ids ? ` (${ids})` : ''} — ${itemCount} article${itemCount > 1 ? 's' : ''} pour ${total.toLocaleString()} ${currency}.${walletLine}${addrLine} Les marchands concernés ont été notifiés et vous recontacteront pour la livraison.`);
      clearCart();
      setCheckoutOpen(false);
      setCheckoutAddress('');
      setUseWalletPay(false);
    } else {
      pushAssistant("❌ Je n'ai pas pu finaliser la commande. Vérifiez que les articles ne sont pas en rupture de stock, puis réessayez dans un instant.");
    }
    setPlacing(false);
  }, [user, items, currency, checkoutAddress, placing, clearCart, pushAssistant, useWalletPay, refreshWallet]);

  /**
   * Réservation de TOUT le pack voyage : une commande par commerce,
   * la réservation d'hébergement, les rendez-vous des services réservables
   * et une notification pour chaque commerce concerné.
   */
  const handleReservePack = useCallback(async (pack: TravelPack, messageIndex: number) => {
    if (!user || packBusy !== null) return;
    setPackBusy(messageIndex);
    setLoading(true);
    try {
      const result = await reserveTravelPack(pack, {
        id: user.id,
        name: user.name,
        email: user.email,
        telephone: user.telephone,
      });
      if (result.ok) {
        const refs = result.orderIds.map((id) => `#${id}`).join(', ');
        const lines = [
          `✅ Pack ${pack.destination} réservé : ${pack.items.length} prestation${pack.items.length > 1 ? 's' : ''} pour ${pack.total.toLocaleString('fr-FR')} ${pack.currency}${refs ? ` (commande${result.orderIds.length > 1 ? 's' : ''} ${refs})` : ''}.`,
          result.hotels > 0 ? '🏨 Hébergement enregistré — le propriétaire confirme la disponibilité.' : '',
          result.appointments > 0 ? `📅 ${result.appointments} rendez-vous créé${result.appointments > 1 ? 's' : ''}.` : '',
          `🔔 ${result.merchants} commerce${result.merchants > 1 ? 's' : ''} notifié${result.merchants > 1 ? 's' : ''}.`,
        ].filter(Boolean);
        pushAssistant(lines.join('\n'));
        setMessages((prev) => prev.map((m, i) => (i === messageIndex ? { ...m, packReserved: true } : m)));
      } else {
        pushAssistant(`❌ Je n'ai pas pu finaliser la réservation du pack${result.error ? ` (${result.error})` : ''}. Réessayez dans un instant.`);
      }
    } catch {
      pushAssistant("❌ La réservation du pack a échoué. Réessayez dans un instant.");
    }
    setPackBusy(null);
    setLoading(false);
  }, [user, packBusy, pushAssistant]);

  // Exécution de l'action RDV UNIQUEMENT après confirmation explicite du client
  // (« confirmez-vous ? »). Évite un clic malheureux.
  const confirmPendingAction = useCallback(async () => {
    const action = pendingAction;
    if (!action) return;
    setPendingAction(null);
    setLoading(true);
    try {
      if (action.kind === 'cancel') {
        await cancelClientAppointment(action.appt);
        const place = [action.appt.merchantName, action.appt.ville].filter(Boolean).join(', ');
        pushAssistant(`✅ C'est annulé : ${formatWhen(action.appt.date)} à ${action.appt.time} — ${action.appt.serviceName}${place ? ` (${place})` : ''}. Le commerçant en a été informé.`);
      } else {
        const outcome = await rescheduleClientAppointment(action.appt, action.date, action.time);
        if (outcome.ok) {
          const place = action.appt.merchantName ? ` (${action.appt.merchantName})` : '';
          pushAssistant(`✅ C'est déplacé ! Nouveau créneau : ${formatWhen(action.date)} à ${action.time} — ${action.appt.serviceName}${place}. Le commerçant en a été informé.`);
        } else if (outcome.reason === 'closed') {
          pushAssistant(`Le ${formatWhen(action.date)}, ${action.appt.serviceName} est fermé. Proposez-moi un autre jour et je déplace votre rendez-vous.`);
        } else if (outcome.alternatives && outcome.alternatives.length > 0) {
          pushAssistant(`Le créneau ${action.time} du ${formatWhen(action.date)} n'est pas libre chez ${action.appt.serviceName}. Créneaux les plus proches : ${outcome.alternatives.join(', ')}. Dites-moi lequel vous convient.`);
        } else {
          pushAssistant(`Aucun créneau disponible le ${formatWhen(action.date)} chez ${action.appt.serviceName}. Proposez-moi une autre date.`);
        }
      }
    } catch {
      pushAssistant("❌ Je n'ai pas pu finaliser l'opération pour le moment. Réessayez dans un instant.");
    }
    setLoading(false);
  }, [pendingAction, pushAssistant]);

  // Ouverture d'un résultat depuis le chat → stat « consultation » ou fiche commerce.
  // Le marchand n'est notifié QUE maintenant (clic réel), plus à l'affichage.
  const openResult = useCallback((r: SearchResult) => {
    // Résultat porteur d'un lien direct (ex. hébergement → fiche du logement) :
    // on ouvre la page concernée plutôt que la fiche générique du commerce.
    const direct = (r as { href?: string }).href;
    if (direct) { navigate(direct); return; }
    if (r.type === 'produit') {
      trackAiChatEvent({
        clientUserId: user?.id ?? null,
        clientName: user?.name ?? null,
        eventType: 'click',
        productId: r.id,
        productName: r.name,
        merchantId: r.boutique?.id ?? null,
        query: lastQueryRef.current,
      });
      if (r.boutique?.id) {
        notifyMerchantOnAiClick({
          userId: user?.id ?? 0,
          userName: user?.name ?? null,
          merchantId: r.boutique.id,
          itemName: r.name,
          itemType: 'produit',
          query: lastQueryRef.current,
        });
      }
    }
    // Un résultat « boutique » ouvre directement la fiche du commerce.
    if (r.type === 'boutique' && r.boutique) {
      notifyMerchantOnAiClick({
        userId: user?.id ?? 0,
        userName: user?.name ?? null,
        merchantId: r.boutique.id,
        itemName: r.boutique.name,
        itemType: 'boutique',
        query: lastQueryRef.current,
      });
      setMerchant(r.boutique);
      return;
    }
    if (r.type === 'service' && r.boutique?.id) {
      notifyMerchantOnAiClick({
        userId: user?.id ?? 0,
        userName: user?.name ?? null,
        merchantId: r.boutique.id,
        itemName: r.name,
        itemType: 'service',
        query: lastQueryRef.current,
      });
    }
    setSelected(r);
  }, [user]);

  /**
   * « Ce n'est pas ce que je cherche » : réajuste la recherche en un clic.
   * On prépare une consigne de correction dans le champ de saisie et on place
   * le curseur au bon endroit, pour que le client précise sa recherche sans
   * tout retaper — et que l'IA garde le fil de la conversation.
   */
  const handleNotWhatISearch = useCallback((r: SearchResult) => {
    setSelected(null);
    setInput(`Ce n'est pas ce que je cherche (« ${r.name} »). Je cherche plutôt : `);
    window.setTimeout(() => inputRef.current?.focus(), 60);
  }, []);

  const handleAction = (r: SearchResult) => {
    const direct = (r as { href?: string }).href;
    if (direct) { navigate(direct); return; }
    if (r.type === 'service_réservation') navigate(`/booking/${r.id}`);
    else if (r.type === 'produit') void addProductToCart(r);
    else if (r.boutique) setMerchant(r.boutique); // service → fiche boutique DANS le modal
    else navigate(`/service/${r.id}`);
  };

  // ── Tour de passe-passe vocal ──
  // Dès que la note vocale est transcrite, on n'insère RIEN dans le champ :
  // le texte part aussitôt à l'IA comme message, et l'affichage montre la
  // note audio (réécoutable) — comme dans n'importe quel chat.
  const handleVoiceNote = useCallback((result: VoiceResult) => {
    void handleSend(result.text, {
      url: result.audioUrl,
      duration: result.durationSeconds,
      blob: result.audioBlob,
      mime: result.mimeType,
    });
  }, [handleSend]);

  // Devise du panier vs devise du portefeuille : le paiement par solde n'est
  // possible que si elles correspondent (le serveur refuse sinon).
  const cartCurrency = normalizeCurrency(currency);
  const walletMismatch = Boolean(wallet && wallet.currency !== cartCurrency);
  const walletTooLow = Boolean(wallet && wallet.balance < subtotal);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Barre mince : localisation à gauche, sélecteur de devise à droite. */}
      <div className="flex items-center justify-between gap-2 px-1 pb-2 flex-shrink-0">
        <ChatLocationBadge />
        <div className="relative flex-shrink-0" ref={currencyRef}>
          <button
            type="button"
            onClick={() => setCurrencyOpen((v) => !v)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-background-200/70 text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            title="Convertir l'affichage des prix"
          >
            <i className="ri-money-dollar-circle-line text-base leading-none"></i>
            {chatCurrency ? currencyInfo(chatCurrency).code : 'Monnaie du produit'}
            <i className={`ri-arrow-down-s-line text-xs transition-transform ${currencyOpen ? 'rotate-180' : ''}`}></i>
          </button>
          {currencyOpen && (
            <div className="absolute right-0 top-full mt-1.5 w-60 max-h-72 overflow-y-auto bg-background-50 border border-background-200/70 rounded-lg py-1 z-50">
              <button
                type="button"
                onClick={() => { setChatCurrency(''); setCurrencyOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors cursor-pointer ${
                  !chatCurrency ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i className="ri-price-tag-3-line text-base text-accent-500"></i>
                Monnaie du produit
                {!chatCurrency && <i className="ri-check-line ml-auto text-primary-500 text-sm"></i>}
              </button>
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-400 border-t border-background-200/70 mt-1">
                Convertir dans ma devise
              </div>
              {CURRENCIES.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => { setChatCurrency(c.code); setCurrencyOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors cursor-pointer ${
                    chatCurrency === c.code ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <span className="text-base">{c.flag}</span>
                  <span className="truncate">{c.code}</span>
                  <span className="text-xs text-foreground-400 truncate">{c.label}</span>
                  {chatCurrency === c.code && <i className="ri-check-line ml-auto text-primary-500 text-sm"></i>}
                </button>
              ))}
              <div className="px-3 py-2 mt-1 border-t border-background-200/70 flex items-center gap-1.5 text-[10px] text-foreground-400">
                <i className="ri-refresh-line"></i>
                Conversion basée sur les taux de change en direct
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mini-récap panier interactif (géré sans quitter la conversation) */}
      {count > 0 && (
        <div className="mb-3 bg-background-50 border border-primary-200/60 rounded-xl overflow-hidden flex-shrink-0">
          <button
            type="button"
            onClick={() => setCartOpen((v) => !v)}
            className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-background-100/60 transition-colors"
          >
            <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
              <i className="ri-shopping-cart-2-line"></i>
            </span>
            <span className="flex-1 min-w-0 text-left">
              <span className="block text-sm font-semibold text-foreground-900">
                Mon panier · {count} article{count > 1 ? 's' : ''}
              </span>
              <span className="block text-xs text-foreground-500">
                Total {moneyIn(subtotal, currency)} · devise du vendeur — modifiez la quantité sans quitter le chat
              </span>
            </span>
            <i className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform ${cartOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {cartOpen && (
            <div className="border-t border-background-200/60">
              <div className="divide-y divide-background-200/50">
                {items.map((item) => (
                  <div key={item.product.id} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="w-11 h-11 rounded-lg bg-background-100 overflow-hidden flex-shrink-0">
                      {item.product.media ? (
                        <img
                          src={item.product.media}
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-shopping-bag-3-line text-foreground-300"></i>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-800 truncate">{item.product.name}</p>
                      <p className="text-xs text-foreground-500">
                        {convertParts(item.product.price * item.quantity, item.product.currency || currency, chatCurrency).main}
                        {item.quantity > 1 && (
                          <span className="text-foreground-400">
                            {' · '}
                            {convertParts(item.product.price, item.product.currency || currency, chatCurrency).main} l&apos;unité
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.product.id, -1)}
                        className="w-7 h-7 flex items-center justify-center rounded-full border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                        title="Diminuer"
                      >
                        <i className="ri-subtract-line text-sm"></i>
                      </button>
                      <span className="w-7 text-center text-sm font-semibold text-foreground-900">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.product.id, 1)}
                        className="w-7 h-7 flex items-center justify-center rounded-full border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                        title="Augmenter"
                      >
                        <i className="ri-add-line text-sm"></i>
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFromCart(item.product.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
                      title="Retirer du panier"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-3 bg-background-100/50">
                <button
                  type="button"
                  onClick={() => clearCart()}
                  className="text-xs text-foreground-500 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Vider le panier
                </button>
                <button
                  type="button"
                  onClick={() => { setCartOpen(false); handleSend('passe ma commande'); }}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-check-double-line"></i>
                  Passer la commande
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Chat container */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl flex flex-col flex-1 min-h-0 overflow-hidden">
        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, i) => (
            <div key={i}>
              <div className={`flex items-start gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'assistant' ? 'bg-accent-100 text-accent-600' : 'bg-background-200/60 text-foreground-500'
                }`}>
                  <i className={`ri-${msg.role === 'assistant' ? 'robot-2' : 'user'}-line text-sm`}></i>
                </div>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-lg text-sm ${
                  msg.role === 'assistant'
                    ? 'bg-background-100 text-foreground-800'
                    : 'bg-accent-100/70 text-foreground-800'
                }`}>
                  {msg.audio ? (
                    <ChatVoiceNote src={msg.audio.url} durationSeconds={msg.audio.duration} transcript={msg.content} />
                  ) : msg.role === 'assistant' ? (
                    <AiRichContent content={msg.content} />
                  ) : (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                  )}
                  {msg.role === 'assistant' && (
                    <div className="mt-1.5 flex items-center justify-end">
                      <SpeakButton text={msg.content} lang="fr" label="Écouter" />
                    </div>
                  )}
                </div>
              </div>

              {/* Search results cards (cliquables → modal détail) */}
              {msg.results && msg.results.length > 0 && (
                <div className="mt-3 ml-11 space-y-2">
                  {msg.results.map((result, ri) => (
                    <div
                      key={`${result.type}-${result.id}-${ri}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => openResult(result)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openResult(result); } }}
                      className="w-full text-left bg-background-50 border border-background-200/70 rounded-lg p-3 hover:border-accent-300 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative w-14 h-14 rounded-lg bg-background-100 flex-shrink-0 overflow-hidden">
                          {firstChatImage({ media: result.media, image: result.image, videos: result.videos }) ? (
                            <img
                              src={firstChatImage({ media: result.media, image: result.image, videos: result.videos })}
                              alt={result.name}
                              className="w-full h-full object-cover"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <i className={`${resultIcon(result.type)} text-foreground-300 text-lg`}></i>
                            </div>
                          )}
                          {countVideos(result) > 0 && (
                            <span className="absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 flex items-center justify-center">
                              <i className="ri-play-fill text-white text-[9px]"></i>
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleFavoriteResult(result); }}
                            className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-background-50/90 flex items-center justify-center cursor-pointer hover:bg-background-50 transition-colors"
                            title={isFav(favoriteEntityType(result.type), result.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                            aria-label="Ajouter aux favoris"
                          >
                            <i className={`text-[11px] ${isFav(favoriteEntityType(result.type), result.id) ? 'ri-heart-3-fill text-red-500' : 'ri-heart-3-line text-foreground-500'}`}></i>
                          </button>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-foreground-800 truncate">{result.name}</p>
                              {result.description && (
                                <p className="text-xs text-foreground-500 mt-0.5 line-clamp-2">{result.description}</p>
                              )}
                            </div>
                            {result.price ? (
                              (() => {
                                const pp = convertParts(effectivePrice(result), productCurrency(result), chatCurrency);
                                return (
                                  <span className="text-sm font-bold text-accent-600 whitespace-nowrap text-right">
                                    {pp.main}
                                    {pp.sub && <span className="block text-[10px] font-normal text-foreground-400">≈ {pp.sub}</span>}
                                  </span>
                                );
                              })()
                            ) : null}
                          </div>

                          <div className="flex flex-wrap items-center gap-2 mt-1.5">
                            {result.type === 'service_réservation' && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-accent-600 bg-accent-50 px-1.5 py-0.5 rounded">
                                <i className="ri-calendar-check-line text-xs"></i>Réservation
                              </span>
                            )}
                            {result.type === 'produit' && isOutOfStock(result) && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                                <i className="ri-forbid-2-line text-xs"></i>Rupture de stock
                              </span>
                            )}
                            {result.duration ? <span className="text-[10px] text-foreground-500">{result.duration} min</span> : null}
                            {(result.ville || result.location) && (
                              <span className="text-[10px] text-foreground-500 flex items-center gap-0.5">
                                <i className="ri-map-pin-line text-xs"></i>{result.ville || result.location}
                              </span>
                            )}
                            {result.category && (
                              <span className="text-[10px] text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded">{result.category}</span>
                            )}
                          </div>

                          {result.boutique && (
                            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-background-200/50">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  notifyMerchantOnAiClick({
                                    userId: user?.id ?? 0,
                                    userName: user?.name ?? null,
                                    merchantId: result.boutique!.id,
                                    itemName: result.boutique!.name,
                                    itemType: 'boutique',
                                    query: lastQueryRef.current,
                                  });
                                  setMerchant(result.boutique!);
                                }}
                                className="flex items-center gap-2 min-w-0 mr-auto cursor-pointer group"
                                title="Voir la fiche du commerce"
                              >
                                <div className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                                  <img
                                    src={storeLogoOr(result.boutique.image)}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      const img = e.target as HTMLImageElement;
                                      if (img.src !== DEFAULT_STORE_LOGO) img.src = DEFAULT_STORE_LOGO;
                                    }}
                                  />
                                </div>
                                <span className="text-[10px] text-foreground-600 font-medium truncate group-hover:text-accent-700 group-hover:underline underline-offset-2">{result.boutique.name}</span>
                                {result.boutique.marketplace?.name && (
                                  <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 flex items-center gap-0.5">
                                    <i className="ri-store-3-line text-[9px]"></i>{result.boutique.marketplace.name}
                                  </span>
                                )}
                                {vendorRatings[result.boutique.id] && (
                                  <VendorRatingBadge
                                    average={vendorRatings[result.boutique.id].average}
                                    count={vendorRatings[result.boutique.id].count}
                                    className="flex-shrink-0"
                                  />
                                )}
                              </button>
                              <span className="text-[10px] text-accent-600 font-semibold whitespace-nowrap flex-shrink-0">{actionLabel(result.type)} →</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {result.type === 'service_réservation' && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAvailability({ id: result.id, name: result.name });
                            }}
                            className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-primary-700 bg-primary-50 px-2 py-1 rounded-full hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                            title="Voir les créneaux libres du calendrier"
                          >
                            <i className="ri-calendar-2-line text-xs"></i>
                            Voir les disponibilités
                          </button>
                        )}
                        {result.type === 'service' && (
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await startOrderWithTarget(result, [result]);
                            }}
                            className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-accent-700 bg-accent-50 px-2 py-1 rounded-full hover:bg-accent-100 transition-colors cursor-pointer whitespace-nowrap"
                            title="Ajouter ce service au panier puis confirmer la commande"
                          >
                            <i className="ri-shopping-cart-2-line text-xs"></i>
                            Commander
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleNotWhatISearch(result); }}
                          className="inline-flex items-center gap-1 text-[10px] font-medium text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer"
                          title="Réajuster la recherche"
                        >
                          <i className="ri-loop-left-line text-xs"></i>
                          Ce n'est pas ce que je cherche
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {msg.pack && (
                <div className="mt-1 sm:ml-11">
                  <TravelPackCard
                    pack={msg.pack}
                    busy={packBusy === i}
                    reserved={!!msg.packReserved}
                    onReserve={() => { void handleReservePack(msg.pack as TravelPack, i); }}
                  />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-robot-2-line text-sm text-accent-600"></i>
              </div>
              <div className="flex gap-1 px-4 py-2.5">
                <span className="w-2 h-2 bg-foreground-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 bg-foreground-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 bg-foreground-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Confirmation explicite avant annulation / déplacement d'un RDV */}
        {pendingAction && (
          <div className="border-t border-primary-200/60 bg-primary-50/40 px-4 py-3 flex-shrink-0">
            <p className="text-sm font-bold text-foreground-900 flex items-center gap-2 mb-1">
              <i className={`${pendingAction.kind === 'cancel' ? 'ri-calendar-close-line' : 'ri-calendar-2-line'} text-primary-600`}></i>
              {pendingAction.kind === 'cancel' ? "Confirmer l'annulation" : 'Confirmer le déplacement'}
            </p>
            <p className="text-xs text-foreground-500 mb-3">
              {pendingAction.kind === 'cancel'
                ? `Annuler : ${formatWhen(pendingAction.appt.date)} à ${pendingAction.appt.time} — ${pendingAction.appt.serviceName}`
                : `Déplacer « ${pendingAction.appt.serviceName} » vers ${formatWhen(pendingAction.date)} à ${pendingAction.time}`}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPendingAction(null)}
                className="flex-1 py-2.5 rounded-full border border-background-200/70 text-foreground-600 text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
              >
                Non, revenir
              </button>
              <button
                type="button"
                onClick={confirmPendingAction}
                className="flex-1 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-1.5"
              >
                <i className="ri-check-line"></i>
                Oui, confirmer
              </button>
            </div>
          </div>
        )}

        {/* Récapitulatif de commande + confirmation (avant création réelle) */}
        {checkoutOpen && count > 0 && (
          <div className="border-t border-primary-200/60 bg-primary-50/40 px-4 py-3 flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-bold text-foreground-900 flex items-center gap-2">
                <i className="ri-receipt-line text-primary-600"></i>
                Récapitulatif de votre commande
              </p>
              <button
                type="button"
                onClick={() => setCheckoutOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                title="Fermer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="max-h-40 overflow-y-auto rounded-lg border border-background-200/60 bg-background-50 divide-y divide-background-200/50 mb-3">
              {items.map((item) => (
                <div key={item.product.id} className="flex items-center gap-3 px-3 py-2">
                  <div className="w-9 h-9 rounded-md bg-background-100 overflow-hidden flex-shrink-0">
                    {item.product.media ? (
                      <img
                        src={item.product.media}
                        alt={item.product.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-shopping-bag-3-line text-foreground-300 text-sm"></i>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground-800 truncate">{item.product.name}</p>
                    <p className="text-[11px] text-foreground-500">×{item.quantity}</p>
                  </div>
                  <span className="text-xs font-semibold text-foreground-800 whitespace-nowrap">
                    {convertParts(item.product.price * item.quantity, item.product.currency || currency, chatCurrency).main}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between px-1 mb-3 text-sm">
              <span className="text-foreground-600">{count} article{count > 1 ? 's' : ''}</span>
              <span className="font-bold text-foreground-950">Total : {convertParts(subtotal, currency, chatCurrency).main}</span>
            </div>

            {wallet && (
              <div className="mb-3 rounded-lg border border-background-200/70 bg-background-50 p-2.5">
                <span className="block text-[11px] font-semibold text-foreground-600 mb-1.5">Moyen de paiement</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  <button
                    type="button"
                    onClick={() => setUseWalletPay(false)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium text-left transition-colors cursor-pointer ${
                      !useWalletPay
                        ? 'border-primary-300 bg-primary-50/60 text-foreground-900'
                        : 'border-background-200/70 text-foreground-600 hover:border-background-300/60'
                    }`}
                  >
                    <i className="ri-hand-coin-line"></i>
                    <span className="flex-1">À la livraison</span>
                  </button>
                  <button
                    type="button"
                    disabled={walletMismatch || walletTooLow}
                    onClick={() => setUseWalletPay(true)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium text-left transition-colors ${
                      useWalletPay
                        ? 'border-primary-300 bg-primary-50/60 text-foreground-900'
                        : 'border-background-200/70 text-foreground-600 hover:border-background-300/60'
                    } ${walletMismatch || walletTooLow ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <i className="ri-wallet-3-line"></i>
                    <span className="flex-1 truncate">Solde Zifek · {wallet.balance.toLocaleString('fr-FR')} {wallet.currency}</span>
                  </button>
                </div>
                {walletMismatch ? (
                  <p className="text-[10px] text-foreground-500 mt-1.5 leading-relaxed">
                    Votre solde est en {wallet.currency} et ce panier en {cartCurrency} : réglez-le à la livraison ou en Mobile Money.
                  </p>
                ) : walletTooLow ? (
                  <p className="text-[10px] text-foreground-500 mt-1.5 leading-relaxed">
                    Solde insuffisant pour ce panier — rechargez votre portefeuille depuis « Mon portefeuille ».
                  </p>
                ) : useWalletPay ? (
                  <p className="text-[10px] text-primary-700 mt-1.5 leading-relaxed">
                    Le montant sera débité de votre solde dès la confirmation, sans passer par Mobile Money.
                  </p>
                ) : null}
              </div>
            )}

            <div className="relative mb-3">
              <i className="ri-map-pin-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={checkoutAddress}
                onChange={(e) => setCheckoutAddress(e.target.value)}
                placeholder="Adresse de livraison (optionnel)"
                className="w-full pl-9 pr-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCheckoutOpen(false)}
                className="flex-1 py-2.5 rounded-full border border-background-200/70 text-foreground-600 text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
              >
                Modifier le panier
              </button>
              <button
                type="button"
                onClick={confirmCheckout}
                disabled={placing}
                className="flex-1 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer flex items-center justify-center gap-1.5"
              >
                {placing ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Envoi...</>
                ) : (
                  <><i className="ri-check-double-line"></i>Confirmer la commande</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Suggestions */}
        {messages.length <= 1 && (
          <div className="px-4 py-2 flex gap-2 overflow-x-auto flex-shrink-0 border-t border-background-200/70">
            {SUGGESTIONS.map((s) => (
              <button
                key={s.text}
                onClick={() => handleSend(s.text)}
                className="flex items-center gap-1.5 px-3 py-2 bg-background-50 border border-background-200/70 rounded-full text-xs text-foreground-600 whitespace-nowrap cursor-pointer hover:bg-background-100 hover:border-accent-300 transition-colors flex-shrink-0"
              >
                <i className={`${s.icon} text-xs text-accent-500`}></i>
                {s.text}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-background-200/70 flex-shrink-0">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
              placeholder="Ex: Je veux me coiffer à Fès..."
              className="flex-1 px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-full text-sm focus:outline-none focus:border-accent-300"
            />
            <VoiceInputButton userId={user?.id} onVoiceResult={handleVoiceNote} />
            <button
              onClick={() => handleSend()}
              disabled={!input.trim() || loading}
              className="w-10 h-10 rounded-full bg-accent-500 text-background-50 flex items-center justify-center cursor-pointer hover:bg-accent-600 disabled:opacity-50 transition-colors flex-shrink-0"
            >
              <i className="ri-send-plane-fill"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Modal détail d'un résultat */}
      {selected && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="relative flex-shrink-0 p-3 pb-0">
              <ChatMediaGallery
                media={resultMedia(selected)}
                alt={selected.name}
                coverClassName="h-48 rounded-lg"
                fallbackUrl={selected.boutique?.image}
              />
              <button
                type="button"
                onClick={() => toggleFavoriteResult(selected)}
                className="absolute top-5 left-5 w-8 h-8 rounded-full bg-background-50/90 flex items-center justify-center cursor-pointer hover:bg-background-50 transition-colors z-20"
                title={isFav(favoriteEntityType(selected.type), selected.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                aria-label="Ajouter aux favoris"
              >
                <i className={`${isFav(favoriteEntityType(selected.type), selected.id) ? 'ri-heart-3-fill text-red-500' : 'ri-heart-3-line text-foreground-600'}`}></i>
              </button>
              <button
                onClick={() => setSelected(null)}
                className="absolute top-5 right-5 w-8 h-8 rounded-full bg-background-50/90 flex items-center justify-center cursor-pointer hover:bg-background-50 transition-colors z-20"
                aria-label="Fermer"
              >
                <i className="ri-close-line text-foreground-700"></i>
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="text-lg font-bold font-heading text-foreground-950">{selected.name}</h3>
                {selected.price ? (() => {
                  const effective = convertParts(effectivePrice(selected), productCurrency(selected), chatCurrency);
                  const original = selected.discount_enabled && selected.discount_price
                    ? convertParts(Number(selected.price) || 0, productCurrency(selected), chatCurrency)
                    : null;
                  return (
                    <div className="text-right flex-shrink-0">
                      <span className="block text-lg font-bold text-accent-600 whitespace-nowrap">{effective.main}</span>
                      {original ? (
                        <span className="block text-xs text-foreground-400 line-through whitespace-nowrap">{original.main}</span>
                      ) : effective.sub ? (
                        <span className="block text-xs text-foreground-400 whitespace-nowrap">≈ {effective.sub}</span>
                      ) : null}
                    </div>
                  );
                })() : null}
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-3">
                {selected.type === 'service_réservation' && (
                  <span className="inline-flex items-center gap-1 text-xs text-accent-600 bg-accent-50 px-2 py-0.5 rounded-full">
                    <i className="ri-calendar-check-line"></i>Réservable
                  </span>
                )}
                {selected.type === 'produit' && isOutOfStock(selected) && (
                  <span className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                    <i className="ri-forbid-2-line"></i>Rupture de stock
                  </span>
                )}
                {selected.type === 'produit' && !isOutOfStock(selected) && selected.stock_tracking_enabled && typeof selected.stock === 'number' && (
                  <span className="inline-flex items-center gap-1 text-xs text-accent-700 bg-accent-50 px-2 py-0.5 rounded-full">
                    <i className="ri-archive-line"></i>{selected.stock} en stock
                  </span>
                )}
                {selected.duration ? <span className="text-xs text-foreground-500">{selected.duration} min</span> : null}
                {(selected.ville || selected.location) && (
                  <span className="text-xs text-foreground-500 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>{selected.ville || selected.location}
                  </span>
                )}
                {selected.category && (
                  <span className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full">{selected.category}</span>
                )}
              </div>

              {selected.description && (
                <p className="text-sm text-foreground-600 leading-relaxed mb-4 whitespace-pre-line">{selected.description}</p>
              )}

              {selected.boutique && (
                <button
                  type="button"
                  onClick={() => {
                    notifyMerchantOnAiClick({
                      userId: user?.id ?? 0,
                      userName: user?.name ?? null,
                      merchantId: selected.boutique!.id,
                      itemName: selected.boutique!.name,
                      itemType: 'boutique',
                      query: lastQueryRef.current,
                    });
                    setMerchant(selected.boutique!);
                  }}
                  className="w-full text-left flex items-center gap-3 p-3 bg-background-100 rounded-lg mb-4 hover:bg-background-200/70 transition-colors cursor-pointer"
                  title="Voir la fiche du commerce"
                >
                  <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img
                      src={storeLogoOr(selected.boutique.image)}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        if (img.src !== DEFAULT_STORE_LOGO) img.src = DEFAULT_STORE_LOGO;
                      }}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground-800 truncate">{selected.boutique.name}</p>
                    <p className="text-[11px] text-foreground-500">
                      {selected.boutique.marketplace?.name
                        ? `Vendeur sur ${selected.boutique.marketplace.name}`
                        : 'Voir la fiche du commerce, ses produits & services'}
                    </p>
                    {vendorRatings[selected.boutique.id] && (
                      <div className="mt-0.5">
                        <VendorRatingBadge
                          average={vendorRatings[selected.boutique.id].average}
                          count={vendorRatings[selected.boutique.id].count}
                        />
                      </div>
                    )}
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400 text-lg"></i>
                </button>
              )}

              {selected.type === 'service_réservation' ? (
                <div className="space-y-2">
                  <button
                    onClick={() => { setBooking({ id: selected.id, name: selected.name }); setSelected(null); }}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-calendar-check-line"></i>
                    Réserver maintenant
                  </button>
                  <button
                    onClick={() => {
                      setAvailability({ id: selected.id, name: selected.name });
                      setSelected(null);
                    }}
                    className="w-full py-3 bg-background-100 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-calendar-2-line"></i>
                    Voir les disponibilités
                  </button>
                  <button
                    onClick={() => {
                      // Le bouton ouvre la fiche du commerce DANS l'application
                      // (au lieu de rediriger vers une page externe), pour
                      // retrouver ses produits, services et la réservation.
                      if (selected.boutique) {
                        notifyMerchantOnAiClick({
                          userId: user?.id ?? 0,
                          userName: user?.name ?? null,
                          merchantId: selected.boutique.id,
                          itemName: selected.boutique.name,
                          itemType: 'boutique',
                          query: lastQueryRef.current,
                        });
                        setMerchant(selected.boutique);
                      } else {
                        navigate(`/service/${selected.id}`);
                      }
                      setSelected(null);
                    }}
                    className="w-full py-3 bg-background-100 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-store-2-line"></i>
                    Voir la page du service
                  </button>
                </div>
              ) : selected.type === 'produit' ? (
                isOutOfStock(selected) ? (
                  <button
                    type="button"
                    disabled
                    className="w-full py-3 bg-background-200/70 text-foreground-400 rounded-full text-sm font-bold cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-forbid-2-line"></i>
                    Produit en rupture de stock
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      const ok = await addProductToCart(selected);
                      if (ok) pushAssistant(`🛒 « ${selected.name} » a été ajouté à votre panier. Dites-moi « passe ma commande » quand vous êtes prêt et je m'occupe de tout.`);
                      setSelected(null);
                    }}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-shopping-cart-2-line"></i>
                    Commander
                  </button>
                )
              ) : selected.type === 'service' ? (
                <div className="space-y-2">
                  <button
                    onClick={async () => {
                      const target = selected;
                      setSelected(null);
                      await startOrderWithTarget(target, [target]);
                    }}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-shopping-cart-2-line"></i>
                    Commander
                  </button>
                  <button
                    onClick={() => { handleAction(selected); setSelected(null); }}
                    className="w-full py-3 bg-background-100 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-store-2-line"></i>
                    Voir la page du service
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { handleAction(selected); setSelected(null); }}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-arrow-right-line"></i>
                  {actionLabel(selected.type)}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fiche commerce (message privé + catalogue + commande, sans quitter le compte) */}
      {merchant && (
        <MerchantCardModal
          merchant={merchant}
          clientUserId={user?.id ?? null}
          clientName={user?.name ?? null}
          lastQuery={lastQueryRef.current}
          onClose={() => setMerchant(null)}
          onAddToCart={async (r) => {
            const ok = await addProductToCart(r);
            if (ok) pushAssistant(`🛒 « ${r.name} » a été ajouté à votre panier depuis la boutique ${merchant.name}. Dites-moi « passe ma commande » quand vous êtes prêt.`);
            return ok;
          }}
          onBook={(id, name) => { setMerchant(null); setBooking({ id, name }); }}
        />
      )}

      {/* Disponibilités réelles du calendrier ZCalendar (créneaux libres) */}
      {availability && (
        <ServiceAvailabilityModal
          serviceId={availability.id}
          serviceName={availability.name}
          onClose={() => setAvailability(null)}
          onBook={(date, time) => {
            const target = availability;
            setAvailability(null);
            setBooking({ id: target.id, name: target.name, date, time });
          }}
        />
      )}

      {/* Réservation directe (date + service pré-remplis, RDV en un clic) */}
      {booking && (
        <QuickBookingModal
          serviceId={booking.id}
          serviceName={booking.name}
          initialDate={booking.date || null}
          initialTime={booking.time || null}
          onClose={() => { setBooking(null); }}
          onViewDetail={() => { navigate(`/booking/${booking.id}`); setBooking(null); }}
        />
      )}
    </div>
  );
}